import { getDayOfWeek } from 'utils/deliveryDates';

// Which delivery time a date resolves to. Shared by the Order Summary dropdown,
// the weekly fee calculation, and "Add to Cart" — they must agree, or the
// customer sees one slot, is charged for another, and a third reaches Airtable.
// This is the meal-plan side, keyed off the delivery fee table; the storefront
// cart's per-line day+time slots live in utils/deliverySlots.

// Preferred time per day-of-week, used only when that slot is offered for the zip
export const DEFAULT_TIME_BY_DAY = {
    sunday: 'Evening',
    monday: 'Evening',
    tuesday: 'Evening',
    wednesday: 'Midday',
    thursday: 'Midday'
};

// Canonical display order for any times we surface
const TIME_ORDER = ['morning', 'midday', 'afternoon', 'evening', 'late'];

const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Times offered for a day + zip, from the fee table (keys: "zip | day | time", lowercase).
// Pools every zip's slots when the zip isn't known yet, so the dropdown never goes empty.
export const getDeliveryTimeOptions = (deliveryFees = {}, dayLower, zip) => {
    const zipLower = (zip || '').toString().trim().toLowerCase();
    const found = new Map(); // lowercaseTime -> displayTime
    Object.keys(deliveryFees).forEach(key => {
        const parts = key.split('|').map(p => p.trim());
        if (parts.length < 3) return;
        const [keyZip, day, time] = parts;
        if (day === dayLower && time && (!zipLower || keyZip === zipLower)) {
            found.set(time, titleCase(time));
        }
    });
    return Array.from(found.entries())
        .sort((a, b) => {
            const ai = TIME_ORDER.indexOf(a[0]);
            const bi = TIME_ORDER.indexOf(b[0]);
            if (ai === -1 && bi === -1) return a[0].localeCompare(b[0]);
            if (ai === -1) return 1;
            if (bi === -1) return -1;
            return ai - bi;
        })
        .map(([, display]) => display);
};

// Effective time for a date: dropdown pick (the most recent thing the customer told
// us, so it always wins) → time already on an order → subscription → the day's
// default, if the zip offers it. `zipHint` covers dates whose orders carry no zip.
export const resolveDeliveryTime = ({
    deliveryFees = {},
    date,
    ordersForDate = [],
    subscriptions = [],
    deliveryTimeOverrides = {},
    zipHint = ''
}) => {
    const dayName = getDayOfWeek(date);
    const dayLower = dayName.toLowerCase();
    const hasOrders = ordersForDate.some(o => o.quantity > 0);
    const matchingSub = subscriptions.find(s => (s.deliveryDay || '').toLowerCase() === dayLower && !s.isSkipped);
    const zipCode = ordersForDate[0]?.zipCode || zipHint || matchingSub?.zipCode || '';
    const subscriptionDeliveryTime = matchingSub?.deliveryTime || '';

    const options = getDeliveryTimeOptions(deliveryFees, dayLower, zipCode);
    const preferred = DEFAULT_TIME_BY_DAY[dayLower];
    const defaultTime = (preferred && options.includes(preferred)) ? preferred : (options[0] || preferred || 'Evening');

    // Subscribed day with no orders: lock to the subscription's time, else the refund
    // would depend on a dropdown pick that never gets saved anywhere.
    if (matchingSub && !hasOrders) {
        return {
            current: subscriptionDeliveryTime || defaultTime,
            options,
            locked: true,
            zipCode,
            dayName,
            subscriptionDeliveryTime
        };
    }

    const existingOrderTime = ordersForDate.find(o => o.deliveryTime)?.deliveryTime;
    const current = deliveryTimeOverrides[date] || existingOrderTime || subscriptionDeliveryTime || defaultTime;

    // Keep the current value selectable even if the fee table doesn't offer it,
    // so we never hide state already persisted on the order.
    const finalOptions = current && !options.some(o => o.toLowerCase() === current.toLowerCase())
        ? [...options, titleCase(current)]
        : options;

    return { current, options: finalOptions, locked: false, zipCode, dayName, subscriptionDeliveryTime };
};
